/* GUIDE + MANUAL ON EVERY MODULE (Max 01/11): "tôi muốn sẽ có icon trên mỗi module … theo ngôn ngữ của system … click
   thì hiện trực tiếp trên màn hình của user hoặc click chuột phải để mở ở new tab … các module đều phải có guide (take a
   tour) và trong đó có step là đọc manual".

   <ModuleHelp> sits at the right of a module's title: a Guide button that walks the screen (TourGuide, steps pointing
   at real controls) and, when the module has one, a Manual button. The Manual button is a real link to the manual's own
   portal page, so a right-click / middle-click / Ctrl-click opens it in a new tab like any link; a plain click opens it
   in a panel over the screen instead, so nobody loses their place. The tour's last step points at the Manual button
   (or, without a manual, at the Guide button to replay). The tour opens by itself once per person per module. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Compass, ExternalLink, X } from 'lucide-react';
import TourGuide from './TourGuide';
import { MANUALS, manualSrc, manualPage } from './manuals';
import './ModuleHelp.css';

export const MANUAL_STEP = { selector: '[data-help-manual]', title: 'xHelp.tourManualT', body: 'xHelp.tourManualB' };
export const REPLAY_STEP = { selector: '[data-help-guide]', title: 'xHelp.tourReplayT', body: 'xHelp.tourReplayB' };

// The panel over the screen. Rendered into <body> so no page's stacking context can sit on it.
export function ManualPanel({ manual, onClose }) {
  const { t, i18n } = useTranslation();
  const src = manualSrc(manual, i18n.language);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  if (!src) return null;
  return createPortal(
    <div className="mh-back" role="dialog" aria-modal="true" aria-label={t('xHelp.manual')} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="mh-panel" data-help-panel>
        <div className="mh-head">
          <BookOpen size={18} aria-hidden="true" />
          <h3>{t('xHelp.manual')}</h3>
          <span className="mh-grow" />
          <a className="mh-link" href={manualPage(manual)} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} aria-hidden="true" /> {t('xHelp.newTab')}</a>
          <button type="button" className="mh-x" onClick={onClose} aria-label={t('xHelp.close')}><X size={18} /></button>
        </div>
        <iframe className="mh-frame" src={src} title={t('xHelp.manual')} key={src} />
      </aside>
    </div>,
    document.body,
  );
}

// The Manual button on its own — for a screen that keeps its own tour.
export function ManualButton({ manual, compact }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!manual || !MANUALS[manual]) return null;
  const onClick = (e) => {
    // a modified click or a middle click is the reader asking for a tab — leave it to the browser
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    setOpen(true);
  };
  return (
    <>
      <a className="pa-btn mh-btn" href={manualPage(manual)} onClick={onClick} title={t('xHelp.manualHint')} data-help-manual>
        <BookOpen size={14} aria-hidden="true" />{compact ? null : <> {t('xHelp.manual')}</>}
      </a>
      {open && <ManualPanel manual={manual} onClose={() => setOpen(false)} />}
    </>
  );
}

/* steps: the module's own tour (TourGuide steps, i18n keys). tourKey: remembers "seen" per person — pass the module's
   existing key when it already had a tour, so nobody gets it twice. ready: the screen has its data, the controls the
   steps point at are drawn (the tour waits for it before opening by itself). onBeforeTour: put the screen in the state
   the tour describes (first tab, no dialog open). */
export default function ModuleHelp({ manual, steps = [], tourKey, ready = true, auto = true, onBeforeTour }) {
  const { t } = useTranslation();
  const [tourOpen, setTourOpen] = useState(false);
  const hasManual = !!(manual && MANUALS[manual]);
  const allSteps = useMemo(() => [...steps, hasManual ? MANUAL_STEP : REPLAY_STEP], [steps, hasManual]);
  const seenKey = tourKey && (tourKey.startsWith('portal') ? tourKey : `portalTourSeen:${tourKey}`);
  const start = useCallback(() => {
    if (onBeforeTour) onBeforeTour();
    window.setTimeout(() => setTourOpen(true), 250);
  }, [onBeforeTour]);
  const autoDone = useRef(false);
  useEffect(() => {
    if (!auto || !ready || !seenKey || autoDone.current || !steps.length) return undefined;
    autoDone.current = true;
    let seen = true;
    try { seen = !!localStorage.getItem(seenKey); } catch { seen = true; }
    if (seen) return undefined;
    const id = window.setTimeout(() => setTourOpen(true), 700);
    return () => window.clearTimeout(id);
  }, [auto, ready, seenKey, steps.length]);
  const close = () => {
    setTourOpen(false);
    try { if (seenKey) localStorage.setItem(seenKey, '1'); } catch { /* a private window gets the tour again */ }
  };
  return (
    <div className="mh-bar">
      {steps.length > 0 && (
        <button type="button" className="pa-btn mh-btn" onClick={start} disabled={!ready} title={t('st.tourReopen')} data-help-guide>
          <Compass size={14} aria-hidden="true" /> {t('st.tourButton')}
        </button>
      )}
      <ManualButton manual={manual} />
      {steps.length > 0 && <TourGuide steps={allSteps} open={tourOpen} onClose={close} onFinish={close} />}
    </div>
  );
}
